/**
 * Application Constants
 *
 * This file contains only static configuration constants.
 * All business data is fetched from the API.
 */

/**
 * Base checklist items common to all machine types
 */
const BASE_CHECKLIST = [
  { key: 'bodywork', label: 'Carrosserie' },
  { key: 'tires', label: 'Pneus / Roues' },
  { key: 'lights', label: 'Éclairage / Phares' },
  { key: 'fluid_levels', label: 'Niveaux (Huile, Eau)' },
];

/**
 * Machine-category-specific checklist templates for condition reports (état des lieux).
 * Keys are normalized category names (lowercase). Falls back to 'default' if category not found.
 */
export const CHECKLIST_BY_CATEGORY: Record<string, { key: string; label: string }[]> = {
  'tracteur': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'hydraulics', label: 'Circuit Hydraulique' },
    { key: 'pto', label: 'Prise de Force (PDF)' },
    { key: 'three_point', label: 'Attelage 3 Points' },
  ],
  'pelleteuse': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'hydraulics', label: 'Circuit Hydraulique' },
    { key: 'boom_arm', label: 'Bras / Flèche' },
    { key: 'bucket', label: 'Godet' },
    { key: 'tracks', label: 'Chenilles / Train de Roulement' },
  ],
  'chargeuse': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'hydraulics', label: 'Circuit Hydraulique' },
    { key: 'bucket', label: 'Godet / Fourche' },
    { key: 'articulation', label: 'Articulation Centrale' },
  ],
  'groupe electrogene': [
    ...BASE_CHECKLIST,
    { key: 'alternator', label: 'Alternateur' },
    { key: 'control_panel', label: 'Tableau de Commande' },
    { key: 'fuel_tank', label: 'Réservoir Carburant' },
    { key: 'exhaust', label: 'Échappement' },
    { key: 'cables', label: 'Câblage / Connexions' },
  ],
  'compacteur': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'drum', label: 'Rouleau / Tambour' },
    { key: 'vibration', label: 'Système de Vibration' },
  ],
  'grue': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'boom', label: 'Flèche / Bras' },
    { key: 'winch', label: 'Treuil / Câble' },
    { key: 'stabilizers', label: 'Stabilisateurs' },
    { key: 'hook', label: 'Crochet / Élingues' },
  ],
  'outillage': [
    { key: 'general_state', label: 'État Général' },
    { key: 'functionality', label: 'Fonctionnalité' },
    { key: 'battery', label: 'Batterie / Alimentation' },
    { key: 'accessories', label: 'Accessoires Fournis' },
    { key: 'cables', label: 'Câblage / Connexions' },
    { key: 'safety', label: 'Dispositifs de Sécurité' },
  ],
  'irrigation': [
    ...BASE_CHECKLIST,
    { key: 'pump', label: 'Pompe' },
    { key: 'pipes', label: 'Tuyaux / Raccords' },
    { key: 'sprinklers', label: 'Asperseurs / Goutteurs' },
    { key: 'filter', label: 'Filtre' },
    { key: 'pressure', label: 'Manomètre / Pression' },
  ],
  'semoir': [
    ...BASE_CHECKLIST,
    { key: 'hopper', label: 'Trémie' },
    { key: 'distribution', label: 'Système de Distribution' },
    { key: 'depth_control', label: 'Contrôle de Profondeur' },
    { key: 'markers', label: 'Marqueurs / Jalonneurs' },
    { key: 'transmission', label: 'Transmission / Entraînement' },
  ],
  'moissonneuse': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
    { key: 'cutter_bar', label: 'Barre de Coupe' },
    { key: 'threshing', label: 'Système de Battage' },
    { key: 'sieve', label: 'Grilles / Tamis' },
    { key: 'grain_tank', label: 'Trémie à Grain' },
    { key: 'conveyor', label: 'Convoyeurs' },
  ],
  'drone': [
    { key: 'general_state', label: 'État Général' },
    { key: 'propellers', label: 'Hélices / Rotors' },
    { key: 'battery', label: 'Batterie' },
    { key: 'camera', label: 'Caméra / Capteurs' },
    { key: 'controller', label: 'Télécommande' },
    { key: 'gps', label: 'Module GPS' },
    { key: 'landing_gear', label: 'Train d\'Atterrissage' },
    { key: 'cables', label: 'Câblage / Connexions' },
  ],
  'default': [
    ...BASE_CHECKLIST,
    { key: 'glass', label: 'Vitres / Pare-brise' },
    { key: 'interior', label: 'Intérieur Cabine' },
  ],
};

/**
 * Get the checklist template for a given machine category.
 * Falls back to the default generic checklist.
 */
export function getChecklistForCategory(category?: string): { key: string; label: string }[] {
  if (!category) return CHECKLIST_BY_CATEGORY['default'];
  const normalized = category.toLowerCase().trim();
  // Try exact match first, then partial match
  if (CHECKLIST_BY_CATEGORY[normalized]) return CHECKLIST_BY_CATEGORY[normalized];
  for (const [key, items] of Object.entries(CHECKLIST_BY_CATEGORY)) {
    if (key !== 'default' && (normalized.includes(key) || key.includes(normalized))) {
      return items;
    }
  }
  return CHECKLIST_BY_CATEGORY['default'];
}

/**
 * Legacy export — kept for backwards compatibility.
 * Equivalent to CHECKLIST_BY_CATEGORY['default'].
 */
export const CHECKLIST_TEMPLATE = CHECKLIST_BY_CATEGORY['default'];
